import { getApiErrorMessage } from '@quoruum/frontend-common/api/errorMessage';
import { queryClient } from '@quoruum/frontend-common/api/queryClient';
import {
  hotelActionMakeExcelRateChartMutation,
  hotelActionMakeGsheetsRateChartMutation,
} from '@quoruum/frontend-common/oas/gen/analytics/@tanstack/react-query.gen';
import type {
  HotelActionMakeExcelRateChartData,
  HotelActionShareGsheetsRateChartData,
} from '@quoruum/frontend-common/oas/gen/analytics/types.gen';
import { ANALYTICS_HOTEL_REPORT_TYPES } from '@quoruum/frontend-common/oas/gen/enumValues.gen';
import {
  rateHistoryQueryRatesOptions,
  rateLatestQueryLatestInfiniteOptions,
} from '@quoruum/frontend-common/oas/gen/quoruum/@tanstack/react-query.gen';
import type { HotelBean } from '@quoruum/frontend-common/oas/gen/quoruum/types.gen';
import { useMutation, useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../components/Modal';

const RATES_LOOKBACK_DAYS = 15;

type SelectedHotelRateRow = {
  stayDay: string;
  soldOut: boolean | undefined;
  rateDetailsJson: string;
};

type SelectedStayDayRateDetailRow = {
  id: string;
  timestamp: string;
  rateCode: string;
  roomType: string;
  preFeesAmount: string;
};

type RatesDownloadFormat = 'gsheet' | 'excel';

type RatesDownloadOption = {
  key: string;
  label: string;
  format: RatesDownloadFormat;
  type: HotelActionShareGsheetsRateChartData['query']['type'];
};

const ratesDownloadApiTypes = ANALYTICS_HOTEL_REPORT_TYPES;

const RATES_DOWNLOAD_OPTIONS: ReadonlyArray<RatesDownloadOption> = ratesDownloadApiTypes.flatMap(
  (type) => {
    return [
      {
        key: `gsheet-${type}`,
        label: `Gsheet - ${type.toLowerCase()}`,
        format: 'gsheet' as const,
        type,
      },
      {
        key: `excel-${type}`,
        label: `Excel - ${type.toLowerCase()}`,
        format: 'excel' as const,
        type,
      },
    ];
  }
);

function formatLocalDate(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function buildStayDayRange(endDateIso: string, lookbackDays: number): Array<string> {
  const [year, month, day] = endDateIso.split('-').map(Number);
  const endDate =
    Number.isFinite(year) && Number.isFinite(month) && Number.isFinite(day)
      ? new Date(year, month - 1, day)
      : new Date();
  const days: Array<string> = [];

  for (let index = 0; index < lookbackDays; index += 1) {
    const stayDay = new Date(endDate);
    stayDay.setDate(endDate.getDate() - index);
    days.push(formatLocalDate(stayDay));
  }

  return days;
}

function getNextDay(isoDate: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  const date = new Date(year, (month ?? 1) - 1, day ?? 1);
  date.setDate(date.getDate() + 1);
  return formatLocalDate(date);
}

function isIsoDateString(value: string): boolean {
  return /^\d{4}-\d{2}-\d{2}$/.test(value);
}

type RatesSectionProps = {
  selectedHotelDetails: HotelBean | null;
  selectedHotelId: string | undefined;
};

export function RatesSection({
  selectedHotelDetails,
  selectedHotelId,
}: RatesSectionProps): ReactElement {
  const [selectedRatesEndDate, setSelectedRatesEndDate] = useState(() =>
    formatLocalDate(new Date())
  );
  const [ratesEndDateDraft, setRatesEndDateDraft] = useState(selectedRatesEndDate);
  const [selectedRatesStayDay, setSelectedRatesStayDay] = useState<string | null>(null);
  const [isRatesDownloadPending, setIsRatesDownloadPending] = useState(false);
  const [ratesDownloadError, setRatesDownloadError] = useState<string | null>(null);
  useEffect(() => {
    setSelectedRatesStayDay(null);
  }, [selectedHotelId]);
  useEffect(() => {
    setRatesDownloadError(null);
  }, [selectedHotelId]);
  const ratesDayRange = useMemo(
    () => buildStayDayRange(selectedRatesEndDate, RATES_LOOKBACK_DAYS),
    [selectedRatesEndDate]
  );
  function commitRatesEndDate(value: string): void {
    if (!isIsoDateString(value)) {
      setRatesEndDateDraft(selectedRatesEndDate);
      return;
    }

    if (value !== selectedRatesEndDate) {
      setSelectedRatesEndDate(value);
    }
  }
  const canApplyRatesEndDate =
    isIsoDateString(ratesEndDateDraft) && ratesEndDateDraft !== selectedRatesEndDate;
  async function handleOpenGsheetRates(
    type: HotelActionShareGsheetsRateChartData['query']['type']
  ): Promise<void> {
    if (!selectedHotelId) {
      return;
    }

    setRatesDownloadError(null);
    setIsRatesDownloadPending(true);

    try {
      const gsheetUri = await makeGsheetsRateChartMutation.mutateAsync({
        path: {
          hotelId: selectedHotelId,
        },
        query: {
          type,
        },
      });

      if (typeof gsheetUri !== 'string' || !gsheetUri.trim()) {
        throw new Error('No gsheet URL was returned.');
      }

      const popup = window.open(gsheetUri, '_blank', 'noopener,noreferrer');
      if (!popup) {
        const anchor = document.createElement('a');
        anchor.href = gsheetUri;
        anchor.target = '_blank';
        anchor.rel = 'noopener noreferrer';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
      }
    } catch (error) {
      setRatesDownloadError(getApiErrorMessage(error, 'Failed to open gsheet rate download link.'));
    } finally {
      setIsRatesDownloadPending(false);
    }
  }
  async function handleDownloadExcelRates(
    type: HotelActionMakeExcelRateChartData['query']['type']
  ): Promise<void> {
    if (!selectedHotelId) {
      return;
    }

    setRatesDownloadError(null);
    setIsRatesDownloadPending(true);

    try {
      const fileResponse = await makeExcelRateChartMutation.mutateAsync({
        path: {
          hotelId: selectedHotelId,
        },
        query: {
          type,
        },
      });

      const objectUrl = URL.createObjectURL(fileResponse);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download =
        fileResponse instanceof File && fileResponse.name
          ? fileResponse.name
          : `hotel-${selectedHotelId}-rate-${type}.xlsx`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setRatesDownloadError(
        getApiErrorMessage(error, 'Failed to download excel rate export file.')
      );
    } finally {
      setIsRatesDownloadPending(false);
    }
  }
  const {
    data: selectedHotelRateRows = [],
    error: selectedHotelRatesError,
    isError: hasSelectedHotelRatesError,
    isLoading: isSelectedHotelRatesLoading,
  } = useQuery<SelectedHotelRateRow[]>({
    queryKey: ['hotel-map-selected-hotel-rates', selectedHotelId, ...ratesDayRange],
    enabled: Boolean(selectedHotelId),
    staleTime: 60 * 1000,
    queryFn: async ({ signal }) => {
      if (!selectedHotelId) {
        return [];
      }

      const rows = await Promise.all(
        ratesDayRange.map(async (stayDay) => {
          const generatedOptions = rateLatestQueryLatestInfiniteOptions({
            query: {
              hotelId: selectedHotelId,
              'dayRange.arrival': stayDay,
              'dayRange.departure': getNextDay(stayDay),
              pageSize: 1,
              currencyCode: selectedHotelDetails?.currencyCode ?? 'USD',
            },
          });
          const { queryFn, queryKey } = generatedOptions;

          if (typeof queryFn !== 'function') {
            return {
              stayDay,
              soldOut: undefined,
              rateDetailsJson: '-',
            };
          }

          const firstPage = await queryFn({
            client: queryClient,
            direction: 'forward',
            meta: undefined,
            pageParam: 1,
            queryKey,
            signal,
          });
          const firstRate = firstPage?.results?.[0];
          const firstRateDetail = firstRate?.rateDetails?.[0];

          return {
            stayDay,
            soldOut: firstRate?.noRatesReason === 'SOLD_OUT',
            rateDetailsJson: firstRateDetail ? JSON.stringify(firstRateDetail) : '-',
          };
        })
      );

      return rows;
    },
  });
  const {
    data: selectedRatesStayDayResponse,
    error: selectedRatesStayDayError,
    isError: hasSelectedRatesStayDayError,
    isLoading: isSelectedRatesStayDayLoading,
  } = useQuery({
    ...(selectedHotelId && selectedRatesStayDay
      ? rateHistoryQueryRatesOptions({
          query: {
            hotelId: selectedHotelId,
            'dayRange.arrival': selectedRatesStayDay,
            'dayRange.departure': getNextDay(selectedRatesStayDay),
            pageSize: 10,
            currencyCode: selectedHotelDetails?.currencyCode ?? 'USD',
          },
        })
      : rateHistoryQueryRatesOptions({
          query: {
            hotelId: '',
            'dayRange.arrival': selectedRatesEndDate,
            'dayRange.departure': getNextDay(selectedRatesEndDate),
            pageSize: 10,
            currencyCode: selectedHotelDetails?.currencyCode ?? 'USD',
          },
        })),
    enabled: Boolean(selectedHotelId && selectedRatesStayDay),
    staleTime: 30 * 1000,
  });
  const selectedRatesStayDayRows = useMemo<SelectedStayDayRateDetailRow[]>(() => {
    const rows: SelectedStayDayRateDetailRow[] = [];
    const rates = selectedRatesStayDayResponse?.results ?? [];

    rates.forEach((rate, rateIndex) => {
      const timestamp = rate.timestamp ?? '-';
      const details = rate.rateDetails ?? [];

      if (details.length === 0) {
        rows.push({
          id: `${timestamp}-${rateIndex}`,
          timestamp,
          rateCode: '-',
          roomType: '-',
          preFeesAmount: '-',
        });
        return;
      }

      details.forEach((detail, detailIndex) => {
        rows.push({
          id: `${timestamp}-${rateIndex}-${detailIndex}`,
          timestamp,
          rateCode: detail.rateCode ?? '-',
          roomType: detail.roomTypeCode ?? '-',
          preFeesAmount: detail.rateBasePlusResortFees ?? '-',
        });
      });
    });

    return rows;
  }, [selectedRatesStayDayResponse]);
  const makeGsheetsRateChartMutation = useMutation(hotelActionMakeGsheetsRateChartMutation());
  const makeExcelRateChartMutation = useMutation(hotelActionMakeExcelRateChartMutation());
  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <div className="mb-3 flex items-start justify-end gap-3">
          <div className="flex items-center gap-2">
            <details className="relative">
              <summary className="cursor-pointer list-none rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50">
                Downloads
              </summary>
              <div className="absolute right-0 z-10 mt-1 w-52 rounded-md border border-slate-200 bg-white p-1 shadow-lg">
                {RATES_DOWNLOAD_OPTIONS.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    onClick={() => {
                      if (option.format === 'gsheet') {
                        void handleOpenGsheetRates(option.type);
                        return;
                      }

                      void handleDownloadExcelRates(option.type);
                    }}
                    disabled={isRatesDownloadPending}
                    className="block w-full rounded px-2 py-1 text-left text-xs text-sky-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </details>
          </div>
        </div>

        {ratesDownloadError ? (
          <p className="mb-2 text-xs text-rose-700">{ratesDownloadError}</p>
        ) : null}

        <div className="mb-3">
          <label htmlFor="rates-end-date" className="mb-1 block text-xs font-medium text-slate-700">
            End date (shows selected date and previous 14 days)
          </label>
          <div className="flex items-center gap-2">
            <input
              id="rates-end-date"
              type="date"
              value={ratesEndDateDraft}
              onChange={(event) => {
                setRatesEndDateDraft(event.target.value);
              }}
              className="w-full rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs text-slate-900 shadow-sm outline-none transition focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
            />
            <button
              type="button"
              onClick={() => commitRatesEndDate(ratesEndDateDraft)}
              disabled={!canApplyRatesEndDate}
              className="rounded-md border border-slate-300 bg-white px-2 py-1.5 text-xs font-medium text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50"
            >
              Apply
            </button>
          </div>
        </div>

        {isSelectedHotelRatesLoading ? (
          <p className="text-xs text-slate-600">Loading rates...</p>
        ) : null}
        {hasSelectedHotelRatesError ? (
          <p className="text-xs text-rose-700">
            {getApiErrorMessage(selectedHotelRatesError, 'Unable to load rates.')}
          </p>
        ) : null}

        <div className="mt-3 min-h-0 flex-1 overflow-x-auto overflow-y-auto rounded-md border border-slate-200">
          <table className="w-full table-fixed border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="w-28 px-3 py-2 text-xs font-semibold text-slate-600">stayDay</th>
                <th className="px-3 py-2 text-xs font-semibold text-slate-600">rateDetails</th>
                <th className="w-20 px-3 py-2 text-xs font-semibold text-slate-600">soldOut</th>
              </tr>
            </thead>
            <tbody>
              {selectedHotelRateRows.map((row) => (
                <tr key={row.stayDay} className="border-b border-slate-100 last:border-b-0">
                  <td className="px-3 py-2 text-xs leading-5 text-slate-800">
                    <button
                      type="button"
                      onClick={() => setSelectedRatesStayDay(row.stayDay)}
                      className="text-left text-sky-700 underline hover:text-sky-800"
                    >
                      {row.stayDay}
                    </button>
                  </td>
                  <td className="px-3 py-2 text-xs leading-5 text-slate-800">
                    {(() => {
                      if (row.rateDetailsJson === '-') {
                        return '-';
                      }
                      try {
                        const parsed: unknown = JSON.parse(row.rateDetailsJson);
                        if (
                          typeof parsed !== 'object' ||
                          parsed === null ||
                          Array.isArray(parsed)
                        ) {
                          return row.rateDetailsJson;
                        }
                        const detail = parsed as Record<string, unknown>;
                        const fields: Array<{ key: string; value: unknown }> = [
                          { key: 'rateCode', value: detail.rateCode },
                          { key: 'roomTypeCode', value: detail.roomTypeCode },
                          { key: 'preFeesAmount', value: detail.preFeesAmount },
                          { key: 'postFeesAmount', value: detail.postFeesAmount },
                        ].filter(
                          ({ value }) => value !== undefined && value !== null && value !== ''
                        );
                        if (fields.length === 0) {
                          return '-';
                        }
                        return (
                          <ul className="space-y-0.5">
                            {fields.map(({ key, value }) => (
                              <li key={key}>
                                <span className="font-medium text-slate-600">{key}:</span>{' '}
                                <span>{String(value)}</span>
                              </li>
                            ))}
                          </ul>
                        );
                      } catch {
                        return row.rateDetailsJson;
                      }
                    })()}
                  </td>
                  <td className="px-3 py-2 text-xs leading-5 text-slate-800">
                    {row.soldOut === undefined ? '-' : String(row.soldOut)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Modal
        isOpen={Boolean(selectedRatesStayDay)}
        onClose={() => setSelectedRatesStayDay(null)}
        ariaLabel="Rate details by stay day"
        panelClassName="w-full max-w-4xl rounded-lg border border-slate-200 bg-white p-4 shadow-xl"
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold text-slate-900">Rate Details</h4>
            <p className="mt-1 text-xs text-slate-600">stayDay: {selectedRatesStayDay ?? '-'}</p>
          </div>
          <button
            type="button"
            onClick={() => setSelectedRatesStayDay(null)}
            className="rounded-md border border-slate-300 px-2 py-1 text-xs text-slate-700 hover:bg-slate-50"
          >
            Close
          </button>
        </div>

        {isSelectedRatesStayDayLoading ? (
          <p className="mt-3 text-xs text-slate-600">Loading day rates...</p>
        ) : null}
        {hasSelectedRatesStayDayError ? (
          <p className="mt-3 text-xs text-rose-700">
            {getApiErrorMessage(selectedRatesStayDayError, 'Unable to load day rates.')}
          </p>
        ) : null}

        <div className="mt-3 max-h-[65dvh] overflow-auto rounded-md border border-slate-200">
          <table className="w-full table-fixed border-collapse text-left text-sm">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50">
                <th className="w-48 px-3 py-2 text-xs font-semibold text-slate-600">timestamp</th>
                <th className="w-28 px-3 py-2 text-xs font-semibold text-slate-600">rateCode</th>
                <th className="w-28 px-3 py-2 text-xs font-semibold text-slate-600">roomType</th>
                <th className="w-28 px-3 py-2 text-xs font-semibold text-slate-600">
                  preFeesAmount
                </th>
              </tr>
            </thead>
            <tbody>
              {selectedRatesStayDayRows.length > 0 ? (
                selectedRatesStayDayRows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100 last:border-b-0">
                    <td className="px-3 py-2 text-xs leading-5 text-slate-800">{row.timestamp}</td>
                    <td className="px-3 py-2 text-xs leading-5 text-slate-800">{row.rateCode}</td>
                    <td className="px-3 py-2 text-xs leading-5 text-slate-800">{row.roomType}</td>
                    <td className="px-3 py-2 text-xs leading-5 text-slate-800">
                      {row.preFeesAmount}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="px-3 py-3 text-xs text-slate-600">
                    No rates found for this day.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </Modal>
    </>
  );
}
